import { TooltipProvider } from "@/components/ui/tooltip";
import type { Entity } from "@cohesive/ui-components";
import { Circle, SquareMousePointer, StickyNote, Type } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { CANVAS_KEYS } from "../canvas-controls.config";
import { useCanvasToolbarLayout } from "../context/CanvasToolbarLayoutContext";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";
import { useCanvasShapeAndEdgeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import type { ConnectorPreset } from "../hooks/useConnectorPlacement";
import type { ShapeType } from "../nodes/shapes/index";
import { NoteDocPopover } from "./NoteDocPopover";
import { ShapesPopover } from "./ShapesPopover";
import {
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
  useToolbarPopoverCoordinator,
} from "./toolbar";
import { CanvasToolbarFlyoutAnchor } from "./toolbar/CanvasToolbarFlyoutAnchor";
import type { CanvasInsertMenuActions } from "./toolbar/primary/CanvasInsertMenu";
import { CanvasInsertMenu } from "./toolbar/primary/CanvasInsertMenu";

interface CanvasToolbarProps {
  onToggleTextNoteMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingTextNote: boolean;
  onToggleDocMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingDoc: boolean;
  isNoteMenuOpen: boolean;
  /** Only called when the menu is closed — closing goes through onCancelPlacement. */
  onToggleNoteMenu: () => void;
  onCloseNoteMenu: () => void;
  onToggleTextLabelMode: () => void;
  isPlacingTextLabel: boolean;
  onToggleSectionMode: () => void;
  isPlacingSection: boolean;
  onCancelPlacement?: () => void;
  /** Close toolbar flyouts and add-context without cancelling placement. */
  onDismissToolbarMenus?: () => void;
  isShapesMenuOpen: boolean;
  isPlacingShape: boolean;
  pendingShapeType: ShapeType | null;
  onToggleShapesMenu: () => void;
  onShapeSelect: (shape: ShapeType) => void;
  isConnectorActive: boolean;
  activeConnectorPreset: ConnectorPreset | null;
  onConnectorSelect: (preset: ConnectorPreset) => void;
  isWidgetsMenuOpen: boolean;
  onWidgetsMenuOpenChange: (open: boolean) => void;
  isLinkMenuOpen: boolean;
  onLinkMenuOpenChange: (open: boolean) => void;
  insertMenu: CanvasInsertMenuActions & {
    onIntegrationEntitySelect?: (entity: Entity) => void;
  };
}

export function CanvasToolbar({
  onToggleTextNoteMode,
  isPlacingTextNote,
  onToggleDocMode,
  isPlacingDoc,
  isNoteMenuOpen,
  onToggleNoteMenu,
  onCloseNoteMenu,
  onToggleTextLabelMode,
  isPlacingTextLabel,
  onToggleSectionMode,
  isPlacingSection,
  onCancelPlacement,
  onDismissToolbarMenus,
  isShapesMenuOpen,
  isPlacingShape,
  pendingShapeType,
  onToggleShapesMenu,
  onShapeSelect,
  isConnectorActive,
  activeConnectorPreset,
  onConnectorSelect,
  isWidgetsMenuOpen,
  onWidgetsMenuOpenChange,
  isLinkMenuOpen,
  onLinkMenuOpenChange,
  insertMenu,
}: CanvasToolbarProps) {
  const { orientation, dock, tooltipSide, containerClassName } =
    useCanvasToolbarLayout();
  const { lastPlacedShape } = useCanvasShapeAndEdgeStyle();

  const { open, setOpen, closeAll, toggleMenu } =
    useToolbarPopoverCoordinator();

  const toolbarInsertMenu = useMemo(
    () => ({
      ...insertMenu,
      onAddWidget: (preset: Parameters<typeof insertMenu.onAddWidget>[0]) => {
        closeAll();
        insertMenu.onAddWidget(preset);
      },
      onAddLinkSubmit: (url: string) => {
        closeAll();
        insertMenu.onAddLinkSubmit(url);
      },
      onNewNote: () => {
        closeAll();
        insertMenu.onNewNote();
      },
      onNewDocument: () => {
        closeAll();
        insertMenu.onNewDocument();
      },
      onUploadFile: () => {
        closeAll();
        insertMenu.onUploadFile();
      },
    }),
    [insertMenu, closeAll],
  );

  useEffect(() => {
    if (!isShapesMenuOpen && open === "shape") {
      setOpen(null);
    }
  }, [isShapesMenuOpen, open, setOpen]);

  const labelActive = isPlacingTextLabel;
  const stickyActive = isNoteMenuOpen || isPlacingTextNote || isPlacingDoc;
  const isShapeFlyoutOpen = open === "shape" || isShapesMenuOpen;
  const shapesActive = isShapeFlyoutOpen || isPlacingShape || isConnectorActive;

  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  return (
    <motion.div
      initial={
        shouldAnimateChromeEnter
          ? dock === "left"
            ? { opacity: 0, x: -20 }
            : { opacity: 0, y: 20 }
          : false
      }
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        opacity: { duration: 0.2 },
        x: { duration: 0.2 },
        y: { duration: 0.2 },
      }}
      className={containerClassName}
    >
      <TooltipProvider>
        <CanvasToolbarShell orientation={orientation} aria-label="Canvas tools">
          {/* DS creation tools */}
          <CanvasToolbarGroup
            orientation={orientation}
            data-onboarding-target="canvas-object-tools"
          >
            <CanvasToolbarButton
              label={
                <>
                  {isPlacingTextLabel ? "Click to place label" : "Text · Label"}{" "}
                  <kbd className="ml-1 rounded border border-current/40 px-1 py-px text-[10px] leading-none">
                    {CANVAS_KEYS.addTextLabel.toUpperCase()}
                  </kbd>
                </>
              }
              tooltipSide={tooltipSide}
              active={labelActive}
              aria-label={
                isPlacingTextLabel ? "Cancel text label" : "Add text label"
              }
              onClick={() => {
                closeAll();
                onCancelPlacement?.();
                onToggleTextLabelMode();
              }}
            >
              <Type className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>

            <CanvasToolbarFlyoutAnchor
              open={isNoteMenuOpen}
              onOpenChange={(next) => {
                if (next) {
                  closeAll();
                  onCancelPlacement?.();
                  onToggleNoteMenu();
                } else {
                  onCancelPlacement?.();
                }
              }}
              trigger={
                <CanvasToolbarButton
                  label={
                    <>
                      {isPlacingTextNote
                        ? "Click to place note"
                        : isPlacingDoc
                          ? "Click to place doc"
                          : "Add Note or Doc"}{" "}
                      <kbd className="ml-1 rounded border border-current/40 px-1 py-px text-[10px] leading-none">
                        {CANVAS_KEYS.addNote.toUpperCase()}
                      </kbd>
                      {!isPlacingTextNote && !isPlacingDoc && (
                        <kbd className="ml-1 rounded border border-current/40 px-1 py-px text-[10px] leading-none">
                          {CANVAS_KEYS.addDoc.toUpperCase()}
                        </kbd>
                      )}
                    </>
                  }
                  tooltipSide={tooltipSide}
                  active={stickyActive}
                  aria-expanded={isNoteMenuOpen}
                  aria-label={
                    isPlacingTextNote
                      ? "Cancel note"
                      : isPlacingDoc
                        ? "Cancel doc"
                        : "Add note or doc"
                  }
                >
                  <StickyNote className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              }
            >
              <NoteDocPopover
                onSelectNote={(screenPos) => {
                  onCloseNoteMenu();
                  onToggleTextNoteMode(screenPos);
                }}
                onSelectDoc={(screenPos) => {
                  onCloseNoteMenu();
                  onToggleDocMode(screenPos);
                }}
              />
            </CanvasToolbarFlyoutAnchor>

            <CanvasToolbarFlyoutAnchor
              open={isShapeFlyoutOpen}
              onOpenChange={(next) => {
                if (next) {
                  onCancelPlacement?.();
                  closeAll();
                  toggleMenu("shape");
                  onToggleShapesMenu();
                } else {
                  closeAll();
                  onCancelPlacement?.();
                }
              }}
              trigger={
                <CanvasToolbarButton
                  label="Shapes"
                  tooltipSide={tooltipSide}
                  active={shapesActive}
                  aria-expanded={isShapeFlyoutOpen}
                >
                  <Circle className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              }
            >
              <ShapesPopover
                activeShape={pendingShapeType ?? lastPlacedShape}
                activeConnectorPreset={
                  isConnectorActive ? activeConnectorPreset : null
                }
                onShapeSelect={(shapeType) => {
                  onShapeSelect(shapeType);
                }}
                onConnectorSelect={onConnectorSelect}
                onClose={() => {
                  closeAll();
                  onCancelPlacement?.();
                }}
              />
            </CanvasToolbarFlyoutAnchor>

            <CanvasToolbarButton
              label={
                <>
                  {isPlacingSection ? "Click to place section" : "Add Section"}{" "}
                  <kbd className="ml-1 rounded border border-current/40 px-1 py-px text-[10px] leading-none">
                    {CANVAS_KEYS.addSection.toUpperCase()}
                  </kbd>
                </>
              }
              tooltipSide={tooltipSide}
              active={isPlacingSection}
              aria-label={isPlacingSection ? "Cancel section" : "Add section"}
              onClick={() => {
                closeAll();
                onCancelPlacement?.();
                onToggleSectionMode();
              }}
            >
              <SquareMousePointer className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>
          </CanvasToolbarGroup>

          <CanvasToolbarDivider orientation={orientation} />

          {/* Insert menu */}
          <CanvasToolbarGroup orientation={orientation}>
            <div data-onboarding-target="connections-entry">
              <CanvasInsertMenu
                variant="toolbar"
                onOpen={() => {
                  closeAll();
                  onDismissToolbarMenus?.();
                }}
                isWidgetsMenuOpen={isWidgetsMenuOpen}
                isLinkMenuOpen={isLinkMenuOpen}
                onWidgetsMenuOpenChange={(open) => {
                  if (open) {
                    closeAll();
                    onCancelPlacement?.();
                    onLinkMenuOpenChange(false);
                  }
                  onWidgetsMenuOpenChange(open);
                }}
                onLinkMenuOpenChange={(open) => {
                  if (open) {
                    closeAll();
                    onCancelPlacement?.();
                    onWidgetsMenuOpenChange(false);
                  }
                  onLinkMenuOpenChange(open);
                }}
                {...toolbarInsertMenu}
              />
            </div>
          </CanvasToolbarGroup>
        </CanvasToolbarShell>
      </TooltipProvider>
    </motion.div>
  );
}
